import type { ChatFileRef, Message, TimelinePart, ToolCallInfo } from "./api";
import type { ArtifactSummary, Skill } from "./store";
import { expandPastes } from "./pastes";
import { activeSkillIds } from "./skill-gate";

export type ApiMessageContent =
  | string
  | Array<{ type: string; text?: string; image_url?: { url: string } }>;

type ApiToolCall = { id: string; type: "function"; function: { name: string; arguments: string } };

export type ApiMessage = {
  role: string;
  content: ApiMessageContent;
  tool_calls?: ApiToolCall[];
  tool_call_id?: string;
};

const REPLAYED_RESULT_CHARS = 8000;

type SystemContentInput = {
  systemPrompt: string;
  contextBlock: string;
  skills: Skill[];
  activePinnedSkillId: string | null;
  activeActivatedSkillIds: string[];
  artifacts: ArtifactSummary[];
  effort: "off" | "low" | "medium" | "high";
  workspacePath?: string | null;
};

export function buildSystemContent(input: SystemContentInput): string {
  const {
    systemPrompt,
    contextBlock,
    skills,
    activePinnedSkillId,
    activeActivatedSkillIds,
    artifacts,
    effort,
    workspacePath,
  } = input;

  const activeIds = activeSkillIds(activePinnedSkillId, activeActivatedSkillIds);
  const activeSkills = skills.filter((s) => activeIds.has(s.id));
  const offeredSkills = skills.filter((s) => s.autoActivate && !activeIds.has(s.id));

  const skillsAddendum = offeredSkills.length > 0
    ? "Available skills (call activate_skill with the name to load):\n" +
      offeredSkills
        .map((s) => `- ${s.name}${s.description ? ` — ${s.description}` : ""}`)
        .join("\n")
    : "";

  const activeSkillBlocks = activeSkills
    .map((s) => `## Active skill: ${s.name}\n${s.systemPrompt}`.trim())
    .filter(Boolean);

  const artifactsBlock = artifacts.length > 0
    ? "## Artifacts in this chat\n" +
      "When the user asks you to iterate on a previous artifact, call artifact_edit (for small changes) or artifact_write (whole-file) with the matching id below. Do NOT create a new artifact unless the user asks for a fresh one.\n" +
      artifacts
        .map((a) => `- ${a.name} — id: ${a.id} (${a.fileCount} file${a.fileCount === 1 ? "" : "s"})`)
        .join("\n")
    : "";

  const effortBlock = effort === "off"
    ? "## Reasoning\nDo not produce any <think> blocks or visible chain-of-thought. Answer directly."
    : "";

  const workspaceBlock = workspacePath
    ? `## Workspace mode\nA workspace folder is attached to this chat:\n  ${workspacePath}\n\n` +
      "**You are operating on real files on disk, not in the chat.** When the user asks you to create, modify, or fix code/config/docs in this folder, you MUST call workspace_write or workspace_edit instead of printing the code in the chat. Do not paste full file contents into your reply — the user will read the file directly. Reply with a short summary of what changed and which file(s) you touched (e.g. \"Wrote src/foo.ts (new). Added export of `bar()`.\").\n\n" +
      "Workflow:\n" +
      "1. Before editing, read the relevant file(s) with workspace_read or grep with workspace_grep so your edits match existing style.\n" +
      "2. For new files or whole-file rewrites, use workspace_write.\n" +
      "3. For changes to existing files, prefer workspace_edit (one exact substring replacement) over workspace_write.\n" +
      "4. Use shell_exec for git/build/test commands; each new command needs the user's approval the first time.\n\n" +
      "Inline code blocks are still fine for tiny illustrative snippets (a one-liner the user is asking about), or when the user explicitly says \"just show me\" / \"don't write it\". Otherwise: write the file.\n\n" +
      "Tools (paths are relative to the workspace root):\n" +
      "- workspace_root, workspace_list, workspace_read, workspace_search, workspace_grep — read.\n" +
      "- workspace_write, workspace_edit — write.\n" +
      "- shell_exec — run a shell command (sh -c) in the workspace. Avoid destructive commands (rm -rf, sudo); they are blocked."
    : "";

  return [systemPrompt, contextBlock, skillsAddendum, ...activeSkillBlocks, artifactsBlock, workspaceBlock, effortBlock]
    .filter(Boolean)
    .join("\n\n");
}

function fileBlocksFor(files: NonNullable<Message["files"]>): string {
  return files
    .filter((f) => f.type === "text")
    .map((f) => `<file name="${f.name}">\n${f.content}\n</file>`)
    .join("\n\n");
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function attachmentsBlockFor(files: ChatFileRef[]): string {
  if (files.length === 0) return "";
  const lines = files.map((f) => {
    const facts = [f.mime || "unknown type", formatBytes(f.size), f.lines !== null && `${f.lines} lines`].filter(Boolean).join(", ");
    if (f.text !== undefined) return `<file name="${f.name}">\n${f.text}\n</file>`;
    return `- ${f.name} (${facts})${f.preview ? `: "${f.preview}"` : ""}`;
  });
  return (
    "<attachments>\n" + lines.join("\n") + "\n</attachments>\n" +
    "Files listed by name are stored with this chat but not in your context. Read a text file with read_attachment, " +
    "or work on any file in the sandbox at ~/inbox/<name>."
  );
}

function messageToApi(m: Message): ApiMessage {
  const textFiles = m.files?.filter((f) => f.type === "text") ?? [];
  const imageFiles = m.files?.filter((f) => f.type === "image") ?? [];
  const chatFiles = m.files?.filter((f): f is ChatFileRef => f.type === "file") ?? [];
  const fileText = [textFiles.length > 0 ? fileBlocksFor(m.files!) : "", attachmentsBlockFor(chatFiles)].filter(Boolean).join("\n\n");
  const content = expandPastes(m.content, m.files);
  const combinedText = content && fileText
    ? `${content}\n\n${fileText}`
    : content || fileText;

  if (imageFiles.length > 0) {
    const parts: Array<{ type: string; text?: string; image_url?: { url: string } }> = [];
    if (combinedText) parts.push({ type: "text", text: combinedText });
    for (const img of imageFiles) {
      if (img.type === "image") parts.push({ type: "image_url", image_url: { url: img.dataUrl } });
    }
    return { role: m.role, content: parts };
  }
  return { role: m.role, content: combinedText };
}

function replayedResult(result: unknown): string {
  const text = JSON.stringify(result) ?? "null";
  if (text.length <= REPLAYED_RESULT_CHARS) return text;
  return `${text.slice(0, REPLAYED_RESULT_CHARS)}… [truncated, ${text.length - REPLAYED_RESULT_CHARS} more chars]`;
}

function assistantTurnToApi(m: Message, turnIndex: number): ApiMessage[] {
  const parts: TimelinePart[] = m.timeline?.length
    ? m.timeline
    : [
        ...(m.toolCalls ?? []).map((call) => ({ kind: "tool" as const, call })),
        { kind: "content" as const, text: m.content },
      ];

  const out: ApiMessage[] = [];
  let text = "";
  let calls: ToolCallInfo[] = [];
  let callCount = 0;

  const flushCalls = () => {
    if (calls.length === 0) return;
    const ids = calls.map(() => `call_${turnIndex}_${callCount++}`);
    out.push({
      role: "assistant",
      content: text,
      tool_calls: calls.map((call, i) => ({
        id: ids[i]!,
        type: "function",
        function: { name: call.tool, arguments: JSON.stringify(call.args ?? {}) },
      })),
    });
    calls.forEach((call, i) => out.push({ role: "tool", tool_call_id: ids[i]!, content: replayedResult(call.result) }));
    text = "";
    calls = [];
  };

  for (const part of parts) {
    if (part.kind === "tool") {
      calls.push(part.call);
    } else if (part.kind === "content") {
      flushCalls();
      text += part.text;
    }
  }
  flushCalls();
  if (text.trim()) out.push({ role: "assistant", content: text });
  return out;
}

export function buildApiMessages(systemContent: string, history: Message[]): ApiMessage[] {
  const out: ApiMessage[] = [];
  if (systemContent) out.push({ role: "system", content: systemContent });
  history.forEach((m, i) => {
    if (m.role === "assistant" && m.error && !m.content.trim()) return;
    if (m.role === "assistant") out.push(...assistantTurnToApi(m, i));
    else out.push(messageToApi(m));
  });
  return out;
}
